/**
 * useRevocationViewMap — Bundle 1 C2 production data feed hook.
 *
 * Builds the `revocationViewByArtifactId` adapter map that
 * `<OfferedVsCitedDiff>` accepts. Given a list of artifact IDs (e.g. derived
 * from `InspectorChunkDetail.source_id` non-null values), the hook:
 *
 *   1. Fetches each artifact's lifecycle state via GET /artifacts/{id}.
 *      Unknown artifacts (404) and non-REVOKED artifacts contribute nothing
 *      to the map — chip default-safe behavior (no chip) is preserved.
 *
 *   2. For revoked-only artifacts, fetches the citation registry via
 *      GET /artifact_citations/by-artifact/{id}. Uses the first citation
 *      as the chip's citation_id source. (Future enhancement: filter by
 *      citing_entity_type/id once the Inspector route has that context.)
 *
 *   3. Composes the (artifact, citation) pair into the chip module's
 *      CitationView via the pure `toCitationView` mapper.
 *
 * Returns a stable, memoized `Record<string, CitationView>` keyed by
 * artifact_id. Empty when no artifact IDs are supplied OR when none resolve
 * to a revoked-state with a citation record.
 *
 * Action wiring (acknowledge/escalate POST handlers) is NOT included here —
 * this hook produces the READ/DISPLAY view; `onAction` on the chip remains
 * caller-owned and is undefined in the current wiring (next-wave step).
 */
import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'

import {
  type ArtifactBackendResponse,
  type CitationBackendResponse,
  type CitationRevocationApiOptions,
  fetchArtifactView,
  fetchCitationsForArtifact,
  toCitationView,
} from './api'
import type { CitationView } from '../types'

export interface UseRevocationViewMapOptions extends CitationRevocationApiOptions {
  /**
   * When false, all underlying queries are disabled (no fetches fire). Useful
   * for parent components that haven't yet loaded their own data and want to
   * defer revocation lookups. Default: true.
   */
  enabled?: boolean
}

/**
 * Build a memoized `revocationViewByArtifactId` map for the supplied artifact
 * IDs. Safe to call with an empty array — no queries fire and an empty map
 * is returned. Caller is responsible for filtering out null source_ids before
 * passing in.
 */
export function useRevocationViewMap(
  artifactIds: ReadonlyArray<string>,
  opts: UseRevocationViewMapOptions,
): Record<string, CitationView> {
  const enabled = opts.enabled !== false

  // Stable dedup/sort: sorting + de-dup keys the query identity to the SET
  // of IDs requested, not the order they were enumerated by the caller.
  // JSON-array serialization keeps the memo dependency primitive-comparable
  // AND avoids the delimiter-collision risk of a raw character-join key
  // (caller could theoretically pass an id containing the delimiter).
  const uniqueIdsKey = JSON.stringify(
    Array.from(new Set(artifactIds.slice().sort())),
  )
  const uniqueIds = useMemo<string[]>(
    () => JSON.parse(uniqueIdsKey) as string[],
    [uniqueIdsKey],
  )

  // Pass 1: artifact lifecycle state per id (parallel queries).
  const artifactResults = useQueries({
    queries: uniqueIds.map((id) => ({
      queryKey: ['citation-revocation', 'artifact', opts.apiBase, id] as const,
      queryFn: (): Promise<ArtifactBackendResponse | null> =>
        fetchArtifactView(id, opts),
      enabled,
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    })),
  })

  // Derive the subset of REVOKED ids (the only ones that need citation lookup).
  // Recomputes only when the artifact-result `data.status` values change.
  const revokedIdsKey = JSON.stringify(
    artifactResults
      .map((r, i) => (r.data?.status === 'REVOKED' ? uniqueIds[i] : null))
      .filter((s): s is string => s !== null),
  )
  const revokedIds = useMemo<string[]>(
    () => JSON.parse(revokedIdsKey) as string[],
    [revokedIdsKey],
  )

  // Pass 2: citation registry per revoked artifact (conditional second fetch).
  const citationResults = useQueries({
    queries: revokedIds.map((id) => ({
      queryKey: ['citation-revocation', 'citations-by-artifact', opts.apiBase, id] as const,
      queryFn: (): Promise<CitationBackendResponse[]> =>
        fetchCitationsForArtifact(id, opts),
      enabled,
      staleTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    })),
  })

  // Comprehensive value-signature: includes EVERY backend field that
  // `toCitationView` reads (status, revoked_at, title, revoked_reason from
  // the artifact; id, tombstone_acknowledged_at, tombstone_escalated_to_owner_at
  // from the first citation). The memo recomputes whenever ANY of these
  // change — so a live pending → acknowledged → escalated transition (same
  // citation id, only timestamp flips from null to a value) DOES trigger
  // recompute and the chip's ack_status updates in real time. Built as a
  // JSON-array string so the dep is a single primitive value the React-hook
  // linter is happy with.
  const mapSignature = JSON.stringify(
    revokedIds.map((id, i) => {
      const artifactIdx = uniqueIds.indexOf(id)
      const a = artifactResults[artifactIdx]?.data ?? null
      const c = citationResults[i]?.data?.[0] ?? null
      return {
        id,
        a_status: a?.status ?? null,
        a_revoked_at: a?.revoked_at ?? null,
        a_title: a?.title ?? null,
        a_revoked_reason: a?.revoked_reason ?? null,
        c_id: c?.id ?? null,
        c_ack_at: c?.tombstone_acknowledged_at ?? null,
        c_esc_at: c?.tombstone_escalated_to_owner_at ?? null,
        c_count: citationResults[i]?.data?.length ?? null,
      }
    }),
  )

  // Assemble the map. Skipped entries: 404 / not REVOKED / no citation found /
  // mapper returned null (e.g. status=DRAFT). All result in chip-not-rendered.
  return useMemo<Record<string, CitationView>>(() => {
    const map: Record<string, CitationView> = {}
    for (let i = 0; i < revokedIds.length; i++) {
      const id = revokedIds[i]
      const citations = citationResults[i]?.data
      if (citations === undefined || citations.length === 0) continue

      const artifactIdx = uniqueIds.indexOf(id)
      const artifact = artifactResults[artifactIdx]?.data
      if (artifact === undefined || artifact === null) continue

      // Pick the first citation. The by-artifact endpoint returns rows ordered
      // by citing_at; the first is the earliest registered citation for this
      // artifact. For the chip's READ path that's adequate — every citation
      // for a revoked artifact represents the same tombstone fact. Caller-
      // specific filtering (e.g. by citing_entity_id) is a future enhancement.
      const view = toCitationView(artifact, citations[0])
      if (view !== null) map[id] = view
    }
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps -- map identity is
    // keyed by `mapSignature` (a JSON snapshot of every field toCitationView
    // reads). React-query result references churn each poll even when data is
    // unchanged; the signature is the stable source of truth for "did data
    // actually change". See comment above mapSignature for the exact field set.
  }, [mapSignature])
}
